import styled, { css } from 'styled-components';
import { forwardRef } from 'react';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { PatternFormat } from 'react-number-format';

import { config } from '_config';
import { ICONS_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';

import AboutBannerSection from '_components/composed/sections/AboutBannerSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import WhySection from '_components/composed/sections/WhySection';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import LiveChatTextLink from '_components/atomic/LiveChatTextLink';

const { colors, devices, fontSizes, spaces } = theme;
const { publicRuntimeConfig } = getConfig();

const StyledGuaranteePage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  .ourProcessingText {
    margin-bottom: ${spaces.large3X}px;

    p {
      font-family: 'Aeonik';
      font-style: normal;
      font-weight: 400;
      font-size: ${fontSizes.large}px;
      line-height: 150%;
      color: ${colors.gray[700]};
    }
  }

  @media ${devices.tablet} {
    display: block;
    margin-top: ${spaces.regular}px;

    .ourProcessingText p {
      font-size: ${fontSizes.regular}px;
      padding-left: ${spaces.regular}px;
      padding-right: ${spaces.regular}px;
    }
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const GuaranteePage = () => {
  const router = useRouter();
  return (
    <StyledGuaranteePage>
      <AboutBannerSection
        header="Our Guarantee"
        imgSrc={`${config.basePath}/images/guarantee.svg`}
        subHeader={
          <>
            <span>Still have questions? Call</span>&nbsp;
            <a href={`tel:${config.phone}`}>
              <PatternFormat
                value={config.phone?.substring(2)}
                displayType="text"
                format="(###) ###-####"
              />
            </a>
            &nbsp;<span>or</span>
            &nbsp;
            <LiveChatTextLink />
            <span>
              , or send an email to
              <br />
              <a href={`mailto:${config.email}`}>
                support@trademark-genius.com
              </a>{' '}
              for real-time support.
            </span>
          </>
        }
      />
      <WhySection
        isLeftMobileView
        reverse
        leftImage={`${config.basePath}/images/guarantee-why.png`}
        rightPart={
          <div className="ourProcessingText">
            <p>
              The team at Trademark Genius has its customers as its sole focus.
              We are dedicated to providing top notch service and customer
              support to our customers. We will use our knowledge, and software,
              to ensure timely processing of your order, to ensure you the
              fastest submission to the USPTO.
            </p>
            <p>
              However, if you feel we haven’t lived up to our end of the
              bargain, you may request a refund by e-mail, to{' '}
              <a
                href={`mailto:${config.email}?subject=Mail from Guarantee Trademark Site`}
              >
                {config.email}
              </a>
              . Please note that USPTO Government filing fees are
              *non-refundable* under any circumstances once your application has
              been submitted to the United States Patent and Trademark Office.
            </p>
          </div>
        }
      />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => router.push(ROUTES.trademarkRegistration)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            Start Registering Now
          </Button>
        }
        imgSrc={`${config.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle size={686} rotate={-90} left={-63} top={-199} />
      <StyledImageContainer left={-44} top={285}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle
        size={686}
        rotate={-90}
        left={-232}
        top={'calc(53% + 30px)'}
      />
      <StyledImageContainer left={65} top={'calc(71% + 60px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle size={686} rotate={90} right={-199} top={155} />
      <StyledImageContainer right={-66} top={78}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledGuaranteePage>
  );
};

export default GuaranteePage;
